import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, FolderIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { documentService } from '../../services/documentService';
import { DocumentNode } from '../../types/documents';

const isFolderNode = (node: DocumentNode): boolean => {
  return node.nodeType === 'folder';
};

interface TreeItemProps {
  node: DocumentNode;
  depth: number;
}

const TreeItem: React.FC<TreeItemProps> = ({ node, depth }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<DocumentNode[]>(node.children ?? []);
  const [loaded, setLoaded] = useState((node.children ?? []).length > 0);
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    if (!isFolderNode(node))
    {
      navigate(`/documents/${node.id}/details`);
      return;
    }

    const next = !expanded;
    setExpanded(next);

    if (!next || loaded)
    {
      return;
    }

    setLoading(true);
    try {
      const result = await documentService.getDocumentWithChildren(node.path);
      setChildren(result.children ?? []);
      setLoaded(true);
    } catch {
      setChildren([]);
    } finally {
      setLoading(false);
    }
  }, [expanded, loaded, navigate, node]);

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        className="w-full flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-gray-50 text-left"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {isFolderNode(node) ? (
          <ChevronRightIcon
            className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        ) : (
          <span className="h-4 w-4 flex-shrink-0" />
        )}
        {isFolderNode(node) ? (
          <FolderIcon className="h-4 w-4 flex-shrink-0 text-blue-500" />
        ) : (
          <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
        )}
        <span className="text-sm text-gray-700 truncate">{node.title}</span>
      </button>

      {expanded && (
        <div>
          {loading ? (
            <div
              className="text-xs text-gray-400 py-1"
              style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}
            >
              Carregando…
            </div>
          ) : (
            children.map((child) => <TreeItem key={child.id} node={child} depth={depth + 1} />)
          )}
        </div>
      )}
    </div>
  );
};

export const DocumentTreeMini: React.FC = () => {
  const [nodes, setNodes] = useState<DocumentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const root = await documentService.getDocumentWithChildren('/');
        if (active)
        {
          setNodes(root.children ?? []);
        }
      } catch {
        if (active)
        {
          setError(true);
        }
      } finally {
        if (active)
        {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, []);

  if (loading)
  {
    return <div className="text-sm text-gray-400 py-4">Carregando árvore de documentos…</div>;
  }

  if (error)
  {
    return <div className="text-sm text-red-500 py-4">Não foi possível carregar os documentos.</div>;
  }

  if (nodes.length === 0)
  {
    return <div className="text-sm text-gray-400 py-4">Nenhum documento cadastrado ainda.</div>;
  }

  return (
    <div className="max-h-72 overflow-y-auto -mx-2 pr-1">
      {nodes.map((node) => (
        <TreeItem key={node.id} node={node} depth={0} />
      ))}
    </div>
  );
};
